import { parseISO } from 'date-fns'
import React, { createContext, useCallback, useEffect, useState } from 'react'
import {
  Control,
  SubmitHandler,
  useFieldArray,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFieldArrayReplace,
  UseFieldArrayUpdate,
  useForm,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormReset,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import { BsFiletypeXlsx } from 'react-icons/bs'
import { FaClipboard, FaMoneyBill, FaRegFilePdf } from 'react-icons/fa'
import { GiPayMoney } from 'react-icons/gi'
import { MdOutlinePriceChange } from 'react-icons/md'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'

import {
  ClipboardDocumentListIcon,
  HomeModernIcon,
  PlusIcon,
  ReceiptPercentIcon,
  TruckIcon,
} from '@heroicons/react/24/outline'

import {
  BillingDataInterface,
  BillingDetailsDataInterface,
  PreBillingDataInterface,
  TransformOrderDataInterface,
  useServerBilling,
} from '@/api/useServerBilling'
import useServer from '@/api/useServerMain'
import Input from '@/components/forms/Input'
import OrderStatusBadge from '@/components/misc/badges/OrderStatusBadge'
import Button from '@/components/misc/Button'
import GenericTable, {
  DataTableInterface,
  FilterOpts,
} from '@/components/misc/GenericTable'
import Paginate from '@/components/misc/Paginate'
import ScrollTypeFilter from '@/components/misc/ScrollTypeFilter'
import Modal from '@/components/modals/GenericModal'
import Breadcrumb from '@/components/navigation/Breadcrumb'
import { DispatchSelectedModal } from '@/containers/billing/register/DispatchSelectedModal'
import { SelectInterface } from '@/interfaces/InterfacesLocal'
import {
  Regions,
  RegisterBillingInterface,
} from '@/interfaces/ServerInterfaces'
import BillingOrderListPdf from '@/reports/BillingOrderListPdf'
import { useAppSelector } from '@/store/hooks'
import {
  formatAdress,
  formatCurrency,
  formatDateForTable,
  generatePdf,
} from '@/utils/helpers'
import { translateOrderOrigin } from '@/utils/translate'

import { RegisterDetailsContainer } from './RegisterDetailsContainer'
import { WizzardRegisterBilling } from './registers/WizzardRegisterBilling'

// Define interfaces for form data
interface RegisterFormData {
  products: ProductFormData[]
  [key: string]: unknown
}

interface ProductFormData {
  id?: string | number
  name?: string
  quantity?: number
  price?: number
  [key: string]: unknown
}

// Define interface for filter data
interface FilterData {
  page?: number
  dateFrom?: string
  dateTo?: string
  operationNumber?: string | number
  preOperationNumber?: string | number
  name?: string
  isPreReceipt?: string | boolean
  areaSalesId?: string | number
  origin?: string
  clientId?: string | number
  managedById?: string | number
  shippingById?: string | number
  status?: string | string[]
  provinceId?: string | number
  municipalityId?: string | number
  deliveryAt?: string
  billFrom?: string
  billTo?: string
  paymentDeadlineFrom?: string
  paymentDeadlineTo?: string
  minTotalToPay?: number
  maxTotalToPay?: number
  currency?: string
  withCashRegisterOperations?: boolean
  all_data?: boolean
  searchNumber?: string
  includeShipping?: boolean
  [key: string]: string | number | boolean | null | undefined | string[]
}

// Type for filter action data
type FilterActionData = Record<string, string | number | boolean | null>

// Define interface for export modal props
interface ExportModalProps {
  exportAction: (
    _filter: Record<string, string | number | boolean | null>,
    _fileName: string,
    _callBack?: () => void
  ) => Promise<void>
  filters: FilterData
  close: () => void
  isLoading: boolean
}

// Define interface for export form data
interface ExportFormData {
  name: string
  [key: string]: unknown
}

export interface RegisterContextInterface {
  currentStep: number
  setCurrentStep: (_step: number) => void
  watch: UseFormWatch<RegisterFormData>
  getValues: UseFormGetValues<RegisterFormData>
  setValue: UseFormSetValue<RegisterFormData>
  reset: UseFormReset<RegisterFormData>
  control: Control<RegisterFormData>
  handleSubmit: UseFormHandleSubmit<RegisterFormData>
  trigger: UseFormTrigger<RegisterFormData>
  fields: ProductFormData[]
  append: UseFieldArrayAppend<RegisterFormData, never>
  remove: UseFieldArrayRemove
  clearArrayOfProducts: UseFieldArrayReplace<RegisterFormData, never>
  update: UseFieldArrayUpdate<RegisterFormData, never>
  clearErrors: UseFormClearErrors<RegisterFormData>
  isFetching?: boolean
  isSubmit?: boolean
  isLoading: boolean
  isFetchingAux: boolean
  orderById?: RegisterBillingInterface | null
  convertPreBillToBill: (
    _id: number,
    _data: TransformOrderDataInterface
  ) => Promise<void>
  refundBillingOrder: (_id: number) => Promise<void>
  getOrderBillingById: (_id: number | string) => Promise<void>
  updateSingleOrderState: (_order: RegisterBillingInterface) => void
  editBilling: (
    _id: number,
    _data: BillingDataInterface,
    _callBack?: (_data: RegisterBillingInterface) => void
  ) => Promise<void>
  editBillingDetails: (
    _id: number,
    _data: BillingDetailsDataInterface,
    _callBack?: (_data: RegisterBillingInterface) => void
  ) => Promise<void>
  addNewBilling: (
    _newBilling: BillingDataInterface,
    _callback: (_id: number) => void,
    _finallyCase?: () => void
  ) => Promise<void>
  addNewPreBilling: (
    _newPreBilling: PreBillingDataInterface,
    _callback: () => void,
    _finallyCase?: () => void
  ) => Promise<void>
  cancelOrder: (_id: number) => Promise<void>
  close: () => void
  submit?: boolean
  setSubmit?: (_submit: boolean) => void
  deletePartialPayment?: (_id: number, _callback: () => void) => Promise<void>
  setIsLoading?: (_loading: boolean) => void
  openPayModal?: boolean
  setOpenPayModal?: (_open: boolean) => void
  setDetailsRegisterModal?: (_modal: {
    state: boolean
    id: number | null
  }) => void
  TransformPreReceiptIntoReceipt?: (
    _preReceiptId: number,
    _data: Record<string, string | number | boolean | null>,
    _callback: () => void
  ) => Promise<void>
  shippingRegions: Regions[]
  register: ReturnType<typeof useForm<RegisterFormData>>['register']
}

export const RegisterContext = createContext<Partial<RegisterContextInterface>>(
  {}
)

const AllRegistersList = () => {
  // Hooks
  const {
    getAllRegisterBillingList,
    registerBillingList,
    isFetching,
    registerPaginate,
    updateOrderListLocally,
    addNewPreBilling,
    addNewBilling,
    editBilling,
    editBillingDetails,
    isLoading,
    cancelOrder,
    getOrderBillingById,
    orderById,
    updateSingleOrderState,
    convertPreBillToBill,
    refundBillingOrder,
    deletePartialPayment,
    isFetchingAux,
    setIsLoading,
    exportExcelOrders,
    TransformPreReceiptIntoReceipt,
    getAllDeliverers,
  } = useServerBilling()

  const { denyRoles } = useServer()

  const { shippingRegions } = useAppSelector((state) => state.nomenclator)
  const { areas } = useAppSelector((state) => state.nomenclator)

  const [filter, setFilter] = useState<FilterData>({
    page: 1,
  })
  const [isSubmit, setSubmit] = useState<boolean>(false)
  const [wizzardRegisterBillingModal, setWizzardRegisterBillingModal] =
    useState(false)
  const [showDispatchSelectedModal, setShowDispatchSelectedModal] =
    useState(false)
  const [detailsRegisterModal, setDetailsRegisterModal] = useState<{
    state: boolean
    id: number | null
  }>({ state: false, id: null })
  //---------Control for pay modal in register and billing --->
  const [openPayModal, setOpenPayModal] = useState(false)
  const [exportModal, setExportModal] = useState(false)

  const { business } = useAppSelector((state) => state.init)
  useEffect(() => {
    getAllDeliverers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!detailsRegisterModal.state) {
      setOpenPayModal(false)
    }
  }, [detailsRegisterModal])

  const {
    trigger,
    getValues,
    control,
    watch,
    setValue,
    reset,
    clearErrors,
    handleSubmit,
    register,
  } = useForm<RegisterFormData>()
  const { append, fields, remove, update, replace } = useFieldArray({
    name: 'products' as never,
    control,
  })
  const [currentStep, setCurrentStep] = useState<number>(0)

  const currencies =
    business?.availableCurrencies.map((item) => item.code) ?? []

  const contextValues: Partial<RegisterContextInterface> = {
    currentStep,
    setCurrentStep,
    control,
    watch,
    reset,
    setValue,
    fields,
    append,
    remove,
    update,
    trigger,
    getValues,
    clearErrors,
    isFetching,
    addNewBilling,
    addNewPreBilling,
    isLoading,
    editBilling,
    editBillingDetails,
    handleSubmit,
    orderById,
    cancelOrder,
    clearArrayOfProducts: replace,
    getOrderBillingById,
    convertPreBillToBill,
    updateSingleOrderState,
    refundBillingOrder,
    isSubmit,
    setSubmit,
    deletePartialPayment,
    close: () => setWizzardRegisterBillingModal(false),
    isFetchingAux,
    setIsLoading,
    setDetailsRegisterModal,
    setOpenPayModal,
    openPayModal,
    TransformPreReceiptIntoReceipt,
    shippingRegions: shippingRegions,
    register: register,
  }
  // =================================================

  // BillingType
  const billingType: SelectInterface[] = [
    {
      id: 'false',
      name: 'Factura',
    },
    {
      id: 'true',
      name: 'Pre-factura',
    },
  ]
  // BillingStates
  const billingStates: SelectInterface[] = [
    {
      id: 'BILLED',
      name: 'Facturada',
    },
    {
      id: 'PAYMENT_PENDING',
      name: 'Pendiente de pago',
    },
    {
      id: 'CREATED',
      name: 'Creada',
    },
    {
      id: 'OVERDUE',
      name: 'Vencido',
    },
    {
      id: 'REFUNDED',
      name: 'Reembolsado',
    },
    {
      id: 'CANCELLED',
      name: 'Cancelado',
    },
    {
      id: 'ACTIVE',
      name: 'Activa',
    },
    {
      id: 'CLOSED',
      name: 'Cerrada',
    },
    {
      id: 'DISPATCHED',
      name: 'Despachada',
    },
    {
      id: 'RECEIVED',
      name: 'Procesando',
    },
    {
      id: 'COMPLETED',
      name: 'Completada',
    },
    {
      id: 'WAITING',
      name: 'En espera',
    },
    {
      id: 'WITH_ERRORS',
      name: 'Con errores',
    },
    {
      id: 'IN_TRANSIT',
      name: 'En tránsito',
    },
    {
      id: 'DELIVERED',
      name: 'Entregada',
    },
  ]
  // Billing Origin
  const billingOrigin: SelectInterface[] = [
    {
      id: 'admin',
      name: 'Administración',
    },
    {
      id: 'pos',
      name: 'Punto de venta',
    },
    {
      id: 'shop',
      name: 'Tienda online',
    },
    {
      id: 'marketplace',
      name: 'Marketplace',
    },
  ]
  // Table Titles
  const tableTitle = [
    'Tipo',
    'No.',
    'Cliente',
    'Estado',
    'Emisión',
    'Fecha de pago',
    'Origen',
    'Importe',
    'Total productos',
    'Fecha de entrega',
    'Dirección de entrega',
    'Punto de venta',
    'Íconos',
  ]

  const tableData: DataTableInterface[] = []
  if (registerBillingList) {
    registerBillingList?.forEach((order) => {
      tableData.push({
        rowId: order.id,
        payload: {
          Tipo: (
            <div className="flex flex-row justify-start">
              {!order?.isPreReceipt ? (
                <div className="flex gap-2">
                  <FaMoneyBill className="text-green-500" size={20} />
                  {order.partialPayments?.length > 0 &&
                    order.status === 'PAYMENT_PENDING' && (
                      <TooltipG
                        anchorSelect="partialPayment"
                        content="Pago parcial"
                      >
                        {/* <FaMoneyBill1Wave size={20} className="text-green-400" /> */}
                        <GiPayMoney className="text-yellow-500" size={25} />
                      </TooltipG>
                    )}

                  <p>Factura</p>
                </div>
              ) : (
                <div className="flex gap-2">
                  <FaClipboard className="text-amber-500" size={20} />
                  <p>Pre-Factura</p>
                </div>
              )}
            </div>
          ),
          'No.': (
            <div className="flex justify-start">
              {order?.isPreReceipt
                ? `${parseISO(order.createdAt).getFullYear()}/${
                    order.preOperationNumber
                  }`
                : `${parseISO(order.createdAt).getFullYear()}/${
                    order.operationNumber
                  }`}
            </div>
          ),
          Cliente: (
            <div className="flex flex-row justify-start">
              {order?.client?.firstName || order?.client?.lastName
                ? `${order?.client?.firstName ?? ''} ${
                    order?.client?.lastName ?? ''
                  }`
                : (order?.client?.email ?? 'Invitado')}
            </div>
          ),
          Estado: (
            <div className="flex flex-col">
              {
                <div>
                  <OrderStatusBadge status={order?.status} />
                </div>
              }
            </div>
          ),
          Emisión: (
            <div className="flex flex-col">
              {formatDateForTable(order?.createdAt)}
            </div>
          ),
          'Fecha de pago': (
            <div className="flex flex-col">
              {order?.status === 'BILLED' && order?.paidAt
                ? formatDateForTable(order?.paidAt as string)
                : '-'}
            </div>
          ),
          Origen: (
            <div className="flex flex-col">
              {translateOrderOrigin(order.origin)}
            </div>
          ),
          Importe: (
            <p className="flex flex-col">
              {order.totalToPay?.map((total) => (
                <p key={total?.codeCurrency}>
                  <span className="text-sm">
                    {formatCurrency(total.amount, total.codeCurrency)}
                  </span>{' '}
                </p>
              ))}
            </p>
          ),
          'Total productos': (
            <p className="flex flex-col">{order?.totalItems ?? '-'}</p>
          ),
          'Fecha de entrega': order?.deliveryAt
            ? formatDateForTable(order.deliveryAt)
            : '-',
          'Dirección de entrega': formatAdress({
            municipality: order.shipping?.municipality?.name,
            province: order.shipping?.province?.name,
            street1: order.shipping?.street_1,
            street2: order.shipping?.street_2,
          }),
          'Punto de venta': <p>{order?.areaSales?.name ?? ''}</p>,
          Íconos: (
            <div className="inline-flex gap-2">
              {order.discount === 100 ? (
                <ReceiptPercentIcon
                  className="h-6"
                  data-tooltip-content="Descuento 100%"
                  data-tooltip-id="my-tooltip"
                />
              ) : order.houseCosted ? (
                <HomeModernIcon
                  className="h-5 text-green-600"
                  data-tooltip-content="Consumo casa"
                  data-tooltip-id="my-tooltip"
                />
              ) : order.isPreDispatched ? (
                <TruckIcon
                  className="h-5 text-amber-600"
                  data-tooltip-content="Predespachado"
                  data-tooltip-id="my-tooltip"
                />
              ) : order.currenciesPayment !== undefined ? (
                order.currenciesPayment.some(
                  (item) => item.paymentWay === 'TRANSFER'
                )
              ) : (
                ''
              )}
              {order.modifiedPrice && (
                <MdOutlinePriceChange
                  style={{ height: '2rem', width: '1.4rem' }}
                />
              )}
              {order.modifiedPrice && (
                <MdOutlinePriceChange
                  style={{ height: '2rem', width: '1.4rem' }}
                />
              )}

              <Tooltip id="my-tooltip" place="top" />
            </div>
          ),
        },
      })
    })
  }

  const tableActions = [
    // {
    //   title: "Exportar a Pdf",
    //   icon: <FaRegFilePdf className="h-5 text-gray-500" />,
    //   action: () =>
    //     reportDownloadHandler(
    //       "Ordenes con productos",
    //       "order_list",
    //       business!,
    //       registerBillingList,
    //       { page: { orientation: "landscape" } }
    //     ),
    // },
    {
      title: 'Predespachar selección',
      icon: <TruckIcon className="h-5 text-gray-500" />,
      action: () => setShowDispatchSelectedModal(true),
    },
    {
      title: 'Exportar a PDF',
      icon: <FaRegFilePdf className="h-5 text-gray-500" />,
      action: () => {
        const toastId = toast.info('Creando Reporte...', {
          isLoading: true,
          autoClose: false,
        })
        generatePdf(
          <BillingOrderListPdf
            business={business}
            filters={{
              dateFrom: String(filter.dateFrom ?? ''),
              dateTo: String(filter.dateTo ?? ''),
            }}
            orders={registerBillingList}
          />
        )
          .then(() => {
            toast.update(toastId, {
              isLoading: false,
              autoClose: 5000,
              render: 'Reporte listo para descargar',
            })
          })
          .catch(() => {
            toast.update(toastId, {
              type: 'error',
              isLoading: false,
              autoClose: 5000,
              render: 'Ocurrió un error creando el Reporte',
            })
          })
      },
    },
    {
      title: 'Exportar a Excel',
      icon: <BsFiletypeXlsx />,
      action: () => {
        if (filter.dateFrom !== undefined) {
          setExportModal(true)
        } else {
          toast.error('Por favor defina un rango de fecha de emisión')
        }
      },
    },
    // {
    //   title: "Importar a Excel",
    //   icon: <FaRegFileExcel className="h-5 text-gray-500" />,
    //   // action: () => setExportModal(true),
    // },
  ]

  if (denyRoles(['AUDITOR'])) {
    tableActions.unshift({
      title: 'Nuevo registro',
      icon: <PlusIcon className="h-5 text-gray-500" />,
      action: () => setWizzardRegisterBillingModal(true),
    })
  }
  // const searching = {
  //   action: (searchNumber: string | null) =>
  //     setFilter(searchNumber ? { searchNumber } : { page: 1 }),
  //   placeholder: "Buscar por número de operación",
  // };
  const { deliverymans } = useAppSelector((state) => state.nomenclator)
  const delivererSelect: SelectInterface[] =
    deliverymans
      ?.filter((item) => item.roles?.some((rol) => rol.name === 'Repartidor'))
      .map((item) => {
        return {
          id: item.id,
          name: item.displayName,
        }
      }) ?? []
  const filterCodeDatePickerRangeCreatedAt = [
    {
      filterCode: 'dateFrom',
      name: 'Desde',
      isUnitlToday: false,
      includingTime: true,
    },
    {
      filterCode: 'dateTo',
      name: 'Hasta',
      isUnitlToday: true,
      includingTime: true,
    },
  ]

  const filterCodeDatePickerRangePaymentDL = [
    {
      filterCode: 'paymentDeadlineFrom',
      name: 'Desde',
      isUnitlToday: false,
      includingTime: true,
    },
    {
      filterCode: 'paymentDeadlineTo',
      name: 'Hasta',
      isUnitlToday: false,
      includingTime: true,
    },
  ]

  const filterCodePriceRange = [
    {
      name: 'Desde',
      filterCode: 'minTotalToPay',
    },
    {
      name: 'Hasta',
      filterCode: 'maxTotalToPay',
    },
    {
      name: 'Moneda',
      filterCode: 'currency',
      currencies: currencies,
    },
  ]

  const areaSalesSelector: SelectInterface[] = areas
    .filter((item) => item.type === 'SALE')
    .map((area) => ({
      id: area.id,
      name: area.name,
    }))

  const billedRange = [
    {
      filterCode: 'billFrom',
      name: 'Desde',
      isUnitlToday: true,
      includingTime: true,
    },
    {
      filterCode: 'billTo',
      name: 'Hasta',
      isUnitlToday: true,
      includingTime: true,
    },
  ]

  const availableFilters: FilterOpts[] = [
    {
      format: 'number',
      filterCode: 'operationNumber',
      name: 'No.Factura',
    },
    {
      format: 'number',
      filterCode: 'preOperationNumber',
      name: 'No.Pre Factura',
    },
    {
      format: 'input',
      filterCode: 'name',
      name: 'Título de Factura',
    },
    {
      format: 'select',
      name: 'Tipo',
      filterCode: 'isPreReceipt',
      data: billingType,
    },
    {
      name: 'Punto de venta',
      filterCode: 'areaSalesId',
      format: 'select',
      data: areaSalesSelector,
    },
    {
      format: 'select',
      name: 'Origen',
      filterCode: 'origin',
      data: billingOrigin,
    },
    {
      name: 'Cliente',
      filterCode: 'clientId',
      format: 'select',
      asyncData: {
        url: '/customer',
        dataCode: ['firstName', 'lastName'],
        idCode: 'id',
      },
    },
    {
      name: 'Comercial',
      filterCode: 'managedById',
      format: 'select',
      asyncData: {
        url: '/security/users',
        dataCode: ['displayName'],
        idCode: 'id',
      },
    },
    {
      format: 'select',
      name: 'Mensajero / Repartidor',
      filterCode: 'shippingById',
      data: delivererSelect,
    },
    {
      format: 'multiselect',
      name: 'Estado',
      filterCode: 'status',
      data: billingStates,
    },
    {
      name: 'Provincia',
      filterCode: 'provinceId',
      format: 'select',
      asyncData: {
        url: '/public/provinces',
        dataCode: ['name'],
        idCode: 'id',
      },
    },
    {
      name: 'Municipio',
      filterCode: 'municipalityId',
      format: 'select',
      asyncData: {
        url: '/public/municipalities',
        dataCode: ['name'],
        idCode: 'id',
      },
    },
    {
      format: 'datepicker',
      filterCode: 'deliveryAt',
      name: 'Fecha de entrega',
    },
    {
      format: 'datepicker-range',
      filterCode: 'bill',
      name: 'Fecha de facturación',
      datepickerRange: billedRange,
      includingTime: true,
    },
    {
      format: 'datepicker-range',
      filterCode: 'dateFrom',
      name: 'Fecha de emisión',
      datepickerRange: filterCodeDatePickerRangeCreatedAt,
      includingTime: true,
    },
    {
      format: 'datepicker-range',
      filterCode: 'paymentDeadlineFrom',
      name: 'Fecha de vencimiento',
      datepickerRange: filterCodeDatePickerRangePaymentDL,
      includingTime: true,
    },
    {
      format: 'price-range',
      filterCode: 'toPay',
      name: 'Rango de importe',
      priceRange: filterCodePriceRange,
    },
    {
      format: 'boolean',
      name: 'Facturas con efectivo',
      filterCode: 'withCashRegisterOperations',
    },
    {
      format: 'boolean',
      name: 'Todos los datos',
      filterCode: 'all_data',
    },
    // {
    //   format: "datepicker",
    //   filterCode: "importFrom",
    //   name: "Importe desde",
    // },
    // {
    //   format: "datepicker",
    //   filterCode: "importTo",
    //   name: "Importe hasta",
    // },

    // {
    //   format: "select",
    //   name: "Moneda",
    //   filterCode: "currency",
    //   data: currenciesList
    // },
  ]

  const filterAction = (data: FilterActionData | null) => {
    if (data) {
      setFilter({ ...data } as FilterData)
    } else {
      setFilter({ page: 1 })
    }
  }

  const refreshList = useCallback(() => {
    getAllRegisterBillingList({ ...filter, includeShipping: true } as Record<
      string,
      string | number | boolean | null
    >)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  useEffect(() => {
    refreshList()
  }, [filter, refreshList])

  const closeWizzardRegisterBillingModal = () => {
    setWizzardRegisterBillingModal(false)
    setCurrentStep(0)
    replace([])
    reset()
  }

  return (
    <div>
      <Breadcrumb
        icon={<ClipboardDocumentListIcon className="h-6 text-gray-500" />}
        paths={[{ name: 'Facturación' }, { name: 'Registros' }]}
      />
      <ScrollTypeFilter
        current={filter?.status as string}
        items={billingStates}
        onChange={(item: string | number | null) =>
          setFilter({
            ...filter,
            status: item as string | string[],
          })
        }
        title="Filtrar registros"
      />
      <GenericTable
        actions={tableActions}
        defaultColumnsShowed={tableTitle
          .map((item, index) => ({
            id: index,
            name: item,
          }))
          .filter((item) =>
            [
              'Tipo',
              'No.',
              'Cliente',
              'Estado',
              'Emisión',
              'Importe',
              'Íconos',
            ].includes(item.name)
          )}
        filterComponent={{ availableFilters, filterAction }}
        loading={isFetching}
        paginateComponent={
          <Paginate
            action={(page: number) => setFilter({ ...filter, page })}
            data={registerPaginate}
          />
        }
        rowAction={(id: number) =>
          setDetailsRegisterModal(() => {
            setIsLoading(true)
            return { state: true, id: id }
          })
        }
        showSpecificColumns={true}
        syncAction={{
          action: () => refreshList(),
          loading: isFetching,
        }}
        tableData={tableData}
        tableTitles={tableTitle}
        // searching={searching}
      />
      <RegisterContext.Provider value={contextValues}>
        {/* NewRegisterModal */}
        {wizzardRegisterBillingModal && (
          <Modal
            close={closeWizzardRegisterBillingModal}
            size="l"
            state={wizzardRegisterBillingModal}
          >
            <WizzardRegisterBilling
              close={() => closeWizzardRegisterBillingModal()}
            />
          </Modal>
        )}

        {/* Details Register Modal */}
        {detailsRegisterModal.state && detailsRegisterModal.id && (
          <Modal
            close={() => setDetailsRegisterModal({ state: false, id: null })}
            size="l"
            state={detailsRegisterModal.state}
          >
            <RegisterDetailsContainer
              closeModalDetails={() =>
                setDetailsRegisterModal({ state: false, id: null })
              }
              id={detailsRegisterModal?.id}
              updateState={updateOrderListLocally}
            />
          </Modal>
        )}
      </RegisterContext.Provider>

      {showDispatchSelectedModal && (
        <Modal
          close={setShowDispatchSelectedModal}
          state={showDispatchSelectedModal}
        >
          <DispatchSelectedModal
            refreshList={refreshList}
            registerBillingList={registerBillingList}
          />
        </Modal>
      )}

      {exportModal && (
        <Modal close={setExportModal} state={exportModal}>
          <ExportModalContainer
            close={() => setExportModal(false)}
            exportAction={exportExcelOrders}
            filters={filter}
            isLoading={isLoading}
          />
        </Modal>
      )}
    </div>
  )
}
export default AllRegistersList

const ExportModalContainer = ({
  exportAction,
  filters,
  close,
  isLoading,
}: ExportModalProps) => {
  const { control, handleSubmit } = useForm<ExportFormData>()
  const submit: SubmitHandler<ExportFormData> = (data) => {
    exportAction(
      {
        dateFrom: filters.dateFrom || null,
        dateTo: filters.dateTo || null,
        all_data: true,
      },
      data.name,
      close
    )
  }
  return (
    <form onSubmit={handleSubmit(submit)}>
      <Input
        control={control as unknown as Control}
        label="Nombre del archivo .xlsx"
        name="name"
        rules={{ required: 'Requerido *' }}
      />
      <div className="flex justify-end py-2">
        <Button
          color="slate-600"
          disabled={isLoading}
          loading={isLoading}
          name="Aceptar"
          type="submit"
        />
      </div>
    </form>
  )
}

interface TooltipProps {
  content: string
  anchorSelect: string // Identificador único para el tooltip
  children: React.ReactNode
}

export const TooltipG: React.FC<TooltipProps> = ({
  children,
  content,
  anchorSelect,
}) => {
  return (
    <>
      <div id={anchorSelect}>{children}</div>
      <Tooltip anchorSelect={`#${anchorSelect}`} content={content}></Tooltip>
    </>
  )
}
